import React, { useMemo, useCallback } from 'react';
import { isEqual } from 'lodash';

import toArray from 'rc-util/lib/Children/toArray';
import { GridColumns, GridColumn } from '../components/DataGridColumn/interfaces';
import { GridProps } from '../interfaces';
import { SortDirection, SortDirections } from '../utils/interfaces';

export const useColumns = (tableProps: GridProps) => {
  const { columns, children } = tableProps;

  const convertChildrenToColumns = (childrenList: React.ReactNode): GridColumns =>
    toArray(childrenList)
      .filter((child) => React.isValidElement(child))
      .map((child) => {
        const { key, props } = child;
        const { children: propsChildren, ...restProps } = props;
        return {
          key,
          ...restProps,
        };
      });

  const getSortDirection = (column: GridColumn): SortDirection | undefined => {
    if (!column.sortable) {
      return undefined;
    }
    const sortRule = (tableProps.sort || []).find((prop) => isEqual(prop.field, column.dataIndex));
    if (sortRule) {
      return SortDirections[sortRule.dir];
    }
    return undefined;
  };

  const getNextSortDirection = (currentSortDirection: SortDirection | undefined) => {
    switch (currentSortDirection) {
      case SortDirections.asc: {
        return SortDirections.desc;
      }
      case SortDirections.desc: {
        return undefined;
      }
      default: {
        return SortDirections.asc;
      }
    }
  };

  const buildSortChangeObject = (rowData: GridColumn) => {
    const sortArr = tableProps.sort || [];
    const result = sortArr.filter((sort) => !isEqual(sort.field, rowData.dataIndex));
    const currentSortDirection = getSortDirection(rowData);
    const nextSortDirection = getNextSortDirection(currentSortDirection);
    if (nextSortDirection) {
      result.push({ field: rowData.dataIndex, dir: nextSortDirection, dateFormat: rowData.dateFormat });
    }
    return result;
  };

  const handleHeaderCellClick = useCallback(
    (data: GridColumn) => {
      if (!data.sortable) {
        return;
      }

      if (tableProps.onSortChange) {
        tableProps.onSortChange(buildSortChangeObject(data));
      }
    },
    [tableProps.onSortChange],
  );

  const addSortProps = (columnList: GridColumns) => {
    if (!tableProps.onSortChange) return columns;

    return columnList.map((column): GridColumn => {
      if (!column.sortable) return column;
      const currentSortDirection = getSortDirection(column);
      const onHeaderCellProperties = column.onHeaderCell ? column.onHeaderCell(column) : {};

      return {
        ...column,
        onHeaderCell: (rowData: GridColumn) => ({
          ...onHeaderCellProperties,
          sortDirection: currentSortDirection,
          sortable: column.sortable,
          onClick: (event) => {
            if (onHeaderCellProperties.onClick) {
              onHeaderCellProperties.onClick(event);
            }
            handleHeaderCellClick(rowData);
          },
        }),
      } as GridColumn;
    });
  };

  return useMemo(() => addSortProps(columns || convertChildrenToColumns(children)), [columns, children]);
};
